import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { contractsAPI } from '@/lib/api';
import { EntityLink } from '@/components/EntityLink';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { ArrowLeft, Upload, Download, FileText, X, Mail, CheckCircle2, RotateCcw, ShieldCheck, Clock, AlertCircle, Pencil, Save, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { SearchableSelect } from '@/components/SearchableSelect';
import { inventoryAPI } from '@/lib/api';

const CURRENCY_SYMBOLS = { INR: '₹', USD: '$', EUR: '€' };

export default function PurchaseOrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  const [po, setPO] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadingReview, setUploadingReview] = useState(false);
  const [sendingAck, setSendingAck] = useState(false);
  const [showAckModal, setShowAckModal] = useState(false);
  const [ackEmails, setAckEmails] = useState('');

  // Close/Reopen state
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [closeRemarks, setCloseRemarks] = useState('');
  const [closing, setClosing] = useState(false);
  const [showReopenDialog, setShowReopenDialog] = useState(false);
  const [reopenRemarks, setReopenRemarks] = useState('');
  const [reopening, setReopening] = useState(false);

  // Edit mode state
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editItems, setEditItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [itemTypeFilter, setItemTypeFilter] = useState('Finished Good');
  const [saving, setSaving] = useState(false);

  // Conversion-factor dialog for edit mode (non-INR currency)
  const [convDialogOpen, setConvDialogOpen] = useState(false);
  const [pendingCurrency, setPendingCurrency] = useState('INR');
  const [convFactorInput, setConvFactorInput] = useState('');

  const handleEditCurrencyChange = (cur) => {
    if (cur === 'INR') {
      setEditForm(prev => ({ ...prev, currency: 'INR', conversion_factor: '1' }));
      return;
    }
    setPendingCurrency(cur);
    setConvFactorInput(editForm.currency === cur ? editForm.conversion_factor : '');
    setConvDialogOpen(true);
  };

  const confirmEditConversionFactor = () => {
    const f = parseFloat(convFactorInput);
    if (!f || f <= 0) { toast.error('Enter a valid conversion factor (greater than 0)'); return; }
    setEditForm(prev => ({ ...prev, currency: pendingCurrency, conversion_factor: convFactorInput }));
    setConvDialogOpen(false);
  };

  const poCurSymbol = CURRENCY_SYMBOLS[po?.currency] || '₹';
  const editCurSymbol = CURRENCY_SYMBOLS[editForm?.currency] || '₹';

  const isManager = isAdmin || user?.app_roles?.contract_mgmt === 'manager';

  useEffect(() => { loadPO(); }, [id]);

  const loadPO = async () => {
    try { const res = await contractsAPI.getPurchaseOrder(id); setPO(res.data); }
    catch { toast.error('PO not found'); navigate('/contracts/purchase-orders'); }
    finally { setLoading(false); }
  };

  // Start editing
  const startEdit = async () => {
    setEditForm({
      po_number: po.po_number || '',
      po_date: po.po_date || '',
      project_number: po.project_number || '',
      value: po.value_entered != null ? po.value_entered : (po.value || 0),
      description: po.description || '',
      remarks: po.remarks || '',
      email_recipients: po.email_recipients || '',
      currency: po.currency || 'INR',
      conversion_factor: (po.conversion_factor != null ? po.conversion_factor : 1).toString(),
      delivery_due_date: po.delivery_due_date || '',
    });
    setEditItems((po.items || []).map(i => ({
      item_id: i.item_id || '', quantity: i.quantity || '',
      // Edit the value in the PO currency (entered), not the canonical INR.
      unit_price: (i.unit_price_entered != null ? i.unit_price_entered : i.unit_price) || '',
      description: i.description || '',
      due_date: i.due_date || '',
    })));
    // Load all items for the dropdown
    if (allItems.length === 0) {
      try {
        const res = await inventoryAPI.listItems({ limit: 500, sort_by: 'item_name', sort_order: 'asc' });
        setAllItems(res.data.items || []);
      } catch { toast.error('Failed to load items'); }
    }
    setEditing(true);
  };

  const cancelEdit = () => { setEditing(false); };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...editForm,
        currency: editForm.currency || 'INR',
        conversion_factor: editForm.currency === 'INR' ? 1 : (parseFloat(editForm.conversion_factor) || 1),
        value: editForm.value ? parseFloat(editForm.value) : 0,
        items: editItems.filter(i => i.item_id && i.quantity).map(i => ({
          item_id: i.item_id, quantity: parseFloat(i.quantity), unit_price: i.unit_price ? parseFloat(i.unit_price) : 0, description: i.description, due_date: i.due_date || '',
        })),
      };
      await contractsAPI.updatePurchaseOrder(id, payload);
      toast.success('Purchase order updated');
      setEditing(false);
      loadPO();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update PO'); }
    finally { setSaving(false); }
  };

  const addEditItem = () => setEditItems(prev => [...prev, { item_id: '', quantity: '', unit_price: '', description: '', due_date: '' }]);
  const removeEditItem = (idx) => { if (editItems.length > 1) setEditItems(prev => prev.filter((_, i) => i !== idx)); };
  const updateEditItem = (idx, field, value) => setEditItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));

  const editCalculatedTotal = editItems.reduce((sum, i) => sum + ((parseFloat(i.quantity) || 0) * (parseFloat(i.unit_price) || 0)), 0);

  useEffect(() => {
    if (editing && editCalculatedTotal > 0) {
      setEditForm(prev => ({ ...prev, value: editCalculatedTotal }));
    }
  }, [editCalculatedTotal, editing]);

  const filteredEditItems = itemTypeFilter === 'All' ? allItems : allItems.filter(i => i.item_type === itemTypeFilter);
  const editItemOptions = filteredEditItems.map(i => ({
    value: i.id, label: `${i.unique_id} - ${i.item_name}`, sub: i.item_type,
    searchStr: `${i.unique_id} ${i.item_name} ${i.external_id || ''} ${i.category || ''}`,
  }));
  const itemTypeOptions = ['Finished Good', 'Semi-Finished Part', 'Raw Material', 'Job Work Item', 'Consumable', 'Fixed Asset', 'Service', 'Scrap', 'All'];

  const handleClosePO = async () => {
    if (!closeRemarks.trim()) { toast.error('Remarks are mandatory'); return; }
    setClosing(true);
    try {
      await contractsAPI.closePurchaseOrder(id, { remarks: closeRemarks.trim() });
      toast.success('Purchase order closed');
      setShowCloseDialog(false);
      setCloseRemarks('');
      loadPO();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to close PO'); }
    finally { setClosing(false); }
  };

  const handleReopenPO = async () => {
    if (!reopenRemarks.trim()) { toast.error('Remarks are mandatory'); return; }
    setReopening(true);
    try {
      await contractsAPI.reopenPurchaseOrder(id, { remarks: reopenRemarks.trim() });
      toast.success('Purchase order reopened');
      setShowReopenDialog(false);
      setReopenRemarks('');
      loadPO();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reopen PO'); }
    finally { setReopening(false); }
  };

  const handleStatusChange = async (status) => {
    try {
      await contractsAPI.updatePurchaseOrder(id, { status });
      setPO(prev => ({ ...prev, status }));
      toast.success(`Status updated to ${status}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('description', file.name);
      await contractsAPI.uploadPOFile(id, formData);
      toast.success('File uploaded');
      loadPO();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleFileDownload = async (file) => {
    try {
      const res = await contractsAPI.downloadPOFile(id, file.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.original_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const handleFileDelete = async (fileId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await contractsAPI.deletePOFile(id, fileId);
      toast.success('File deleted');
      loadPO();
    } catch { toast.error('Failed to delete'); }
  };

  const openAckModal = async () => {
    try {
      const res = await contractsAPI.resolveEmails(id);
      setAckEmails(res.data.emails || '');
    } catch { setAckEmails(''); }
    setShowAckModal(true);
  };

  const handleSendAcknowledgment = async () => {
    if (!ackEmails.trim()) { toast.error('At least one email recipient is required'); return; }
    setSendingAck(true);
    try {
      const res = await contractsAPI.sendAcknowledgment(id, { email_recipients: ackEmails });
      toast.success(`${res.data.message}${res.data.cc ? ` (CC: ${res.data.cc})` : ''}`);
      setShowAckModal(false);
      loadPO();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send acknowledgment'); }
    finally { setSendingAck(false); }
  };

  const handleReviewUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingReview(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('description', 'Signed Contract Review Document');
      await contractsAPI.uploadReviewDocument(id, formData);
      toast.success('Review document uploaded');
      loadPO();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploadingReview(false); e.target.value = ''; }
  };

  const handleCloseReview = async () => {
    if (!window.confirm('Close the contract review? This confirms that the signed review document has been submitted.')) return;
    try {
      await contractsAPI.closeReview(id);
      toast.success('Contract review closed');
      loadPO();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to close review'); }
  };

  const handleReopenReview = async () => {
    if (!window.confirm('Reopen this contract review?')) return;
    try {
      await contractsAPI.reopenReview(id);
      toast.success('Contract review reopened');
      loadPO();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reopen review'); }
  };

  const formatSize = (bytes) => bytes > 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;

  if (loading) return <div className="flex items-center justify-center h-64 text-sm text-muted-foreground">Loading...</div>;
  if (!po) return null;

  const STATUS_COLORS = {
    active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
    awaited: 'bg-amber-50 text-amber-700 border-amber-200',
    completed: 'bg-slate-50 text-slate-600 border-slate-200',
    cancelled: 'bg-red-50 text-red-700 border-red-200',
    closed: 'bg-violet-50 text-violet-700 border-violet-200',
  };

  const REVIEW_STATUS = {
    pending: { label: 'Pending', color: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock },
    open: { label: 'Open', color: 'bg-blue-50 text-blue-700 border-blue-200', icon: AlertCircle },
    closed: { label: 'Closed', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  };

  const reviewStatus = po.review_status || 'pending';
  const reviewInfo = REVIEW_STATUS[reviewStatus] || REVIEW_STATUS.pending;
  const ReviewIcon = reviewInfo.icon;

  return (
    <div className="space-y-4" data-testid="po-detail-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/contracts/purchase-orders')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-heading text-xl font-bold tracking-tight">{po.po_number}</h1>
              <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[po.status] || ''}`}>{po.status?.replace(/_/g, ' ')}</Badge>
            </div>
            <p className="text-xs text-muted-foreground">{po.customer_name} ({po.customer_code})</p>
          </div>
        </div>
        <div className="flex gap-2 items-center">
          {!editing ? (
            <>
              {po.status !== 'closed' && (
                <>
                  <Button size="sm" variant="outline" onClick={startEdit} data-testid="edit-po-btn">
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="destructive" className="bg-violet-600 hover:bg-violet-700" onClick={() => { setCloseRemarks(''); setShowCloseDialog(true); }} data-testid="close-po-btn">
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Close PO
                  </Button>
                  <Select value={po.status} onValueChange={handleStatusChange}>
                    <SelectTrigger className="w-36 h-8 text-xs" data-testid="po-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="awaited">Awaited</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </>
              )}
              {po.status === 'closed' && isAdmin && (
                <Button size="sm" variant="outline" onClick={() => { setReopenRemarks(''); setShowReopenDialog(true); }} data-testid="reopen-po-btn">
                  <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reopen PO
                </Button>
              )}
            </>
          ) : (
            <>
              <Button size="sm" variant="outline" onClick={cancelEdit}>Cancel</Button>
              <Button size="sm" onClick={saveEdit} disabled={saving} data-testid="save-po-edit-btn">
                <Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Closed PO Banner */}
      {po.status === 'closed' && (
        <Card className="border-violet-200 bg-violet-50/50" data-testid="po-closed-banner">
          <CardContent className="py-3 px-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-violet-800 flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" /> PO Closed
                </p>
                <p className="text-[10px] text-violet-700 mt-0.5">
                  Closed by {po.closed_by_name || 'N/A'} on {po.closed_at?.slice(0, 10) || 'N/A'}
                </p>
                <p className="text-[10px] text-violet-600 mt-1">Remarks: {po.close_remarks || '-'}</p>
              </div>
              {po.dispatched_summary?.length > 0 && (
                <div className="text-right">
                  <p className="text-[10px] font-medium text-violet-700 mb-1">Dispatch Summary</p>
                  {po.dispatched_summary.map((ds, i) => (
                    <p key={i} className="text-[10px] text-violet-600">
                      {ds.item_name}: <span className="font-mono">{ds.dispatched_qty}/{ds.ordered_qty}</span>
                    </p>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-xs font-medium text-muted-foreground">PO Details</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {editing ? (
              <div className="space-y-2">
                <div>
                  <Label className="text-[10px] text-muted-foreground">PO Number</Label>
                  <Input value={editForm.po_number} onChange={e => setEditForm(f => ({ ...f, po_number: e.target.value }))}
                    className="h-7 text-xs font-mono" data-testid="edit-po-number" />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">PO Date</Label>
                  <Input type="date" value={editForm.po_date} onChange={e => setEditForm(f => ({ ...f, po_date: e.target.value }))}
                    className="h-7 text-xs" data-testid="edit-po-date" />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Project Number</Label>
                  <Input value={editForm.project_number} onChange={e => setEditForm(f => ({ ...f, project_number: e.target.value }))}
                    className="h-7 text-xs" data-testid="edit-po-project" />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Delivery Due Date</Label>
                  <Input type="date" value={editForm.delivery_due_date || ''} onChange={e => setEditForm(f => ({ ...f, delivery_due_date: e.target.value }))}
                    className="h-7 text-xs" data-testid="edit-po-delivery-due-date" />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">PO Currency</Label>
                  <select value={editForm.currency || 'INR'} onChange={e => handleEditCurrencyChange(e.target.value)}
                    className="h-7 text-xs border rounded px-2 bg-white w-full" data-testid="edit-po-currency-select">
                    <option value="INR">INR (₹)</option>
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                  </select>
                  {editForm.currency && editForm.currency !== 'INR' && (
                    <p className="text-[10px] text-emerald-700 mt-1">
                      1 {editForm.currency} = ₹{editForm.conversion_factor} ·{' '}
                      <button type="button" className="underline" onClick={() => handleEditCurrencyChange(editForm.currency)}>edit</button>
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">PO Value ({editCurSymbol}) {editCalculatedTotal > 0 && <span className="text-green-600 ml-1">Auto-calculated</span>}</Label>
                  <Input type="number" value={editForm.value} onChange={e => setEditForm(f => ({ ...f, value: e.target.value }))}
                    className="h-7 text-xs" readOnly={editCalculatedTotal > 0} data-testid="edit-po-value" />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Description</Label>
                  <Input value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                    className="h-7 text-xs" data-testid="edit-po-description" />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Remarks</Label>
                  <Input value={editForm.remarks} onChange={e => setEditForm(f => ({ ...f, remarks: e.target.value }))}
                    className="h-7 text-xs" data-testid="edit-po-remarks" />
                </div>
              </div>
            ) : (
              <>
                <InfoRow label="PO Number" value={po.po_number} mono />
                <InfoRow label="PO Date" value={po.po_date} />
                <InfoRow label="Customer" value={po.customer_name} />
                <InfoRow label="Project #" value={po.project_number} />
                <InfoRow label="Delivery Due Date" value={po.delivery_due_date} mono />
                <InfoRow label="Currency" value={po.currency && po.currency !== 'INR' ? `${po.currency} (1 ${po.currency} = ₹${po.conversion_factor})` : 'INR (₹)'} />
                <InfoRow label="Value" value={po.currency && po.currency !== 'INR'
                  ? `${poCurSymbol}${Number(po.value_entered != null ? po.value_entered : 0).toLocaleString('en-IN')} (≈ ₹${Number(po.value || 0).toLocaleString('en-IN')})`
                  : (po.value ? `₹${po.value.toLocaleString()}` : '-')} />
                <InfoRow label="Description" value={po.description} />
                <InfoRow label="Remarks" value={po.remarks} />
                <InfoRow label="Created By" value={po.created_by_name} />
                <InfoRow label="Date" value={po.created_at?.slice(0, 10)} />
              </>
            )}
          </CardContent>
        </Card>

        {/* Items */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xs font-medium text-muted-foreground">Items ({editing ? editItems.length : (po.items?.length || 0)})</CardTitle>
              {editing && (
                <div className="flex items-center gap-2">
                  <select value={itemTypeFilter} onChange={e => setItemTypeFilter(e.target.value)}
                    className="h-5 text-[10px] border rounded px-1 bg-white" data-testid="edit-item-type-filter">
                    {itemTypeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <Button type="button" size="sm" variant="outline" onClick={addEditItem} className="h-5 text-[10px] px-1.5" data-testid="edit-add-item-btn">
                    <Plus className="w-3 h-3 mr-0.5" /> Add
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {editing ? (
              <div className="space-y-2">
                {editItems.map((ei, idx) => (
                  <div key={idx} className="p-2 rounded border bg-muted/30 space-y-1.5">
                    <div className="flex items-start gap-1.5">
                      <div className="flex-1">
                        <SearchableSelect options={editItemOptions} value={ei.item_id}
                          onSelect={v => updateEditItem(idx, 'item_id', v)}
                          placeholder="Search items..." emptyText="No items." testId={`edit-po-item-${idx}`} />
                      </div>
                      {editItems.length > 1 && (
                        <Button type="button" size="icon" variant="ghost" className="h-6 w-6 mt-1 text-red-500" onClick={() => removeEditItem(idx)} data-testid={`edit-remove-item-${idx}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-4 gap-1.5">
                      <div>
                        <Label className="text-[9px] text-muted-foreground">Qty</Label>
                        <Input type="number" value={ei.quantity} onChange={e => updateEditItem(idx, 'quantity', e.target.value)}
                          className="h-6 text-[10px]" data-testid={`edit-item-qty-${idx}`} />
                      </div>
                      <div>
                        <Label className="text-[9px] text-muted-foreground">Unit Price ({editCurSymbol})</Label>
                        <Input type="number" value={ei.unit_price} onChange={e => updateEditItem(idx, 'unit_price', e.target.value)}
                          className="h-6 text-[10px]" data-testid={`edit-item-price-${idx}`} />
                      </div>
                      <div>
                        <Label className="text-[9px] text-muted-foreground">Due Date</Label>
                        <Input type="date" value={ei.due_date || ''} onChange={e => updateEditItem(idx, 'due_date', e.target.value)}
                          className="h-6 text-[10px]" data-testid={`edit-item-due-date-${idx}`} />
                      </div>
                      <div>
                        <Label className="text-[9px] text-muted-foreground">Total</Label>
                        <div className="h-6 flex items-center text-[10px] font-medium bg-slate-50 border rounded px-1.5">
                          {((parseFloat(ei.quantity) || 0) * (parseFloat(ei.unit_price) || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {editCalculatedTotal > 0 && (
                  <div className="flex justify-end">
                    <div className="text-right bg-violet-50 border border-violet-200 rounded px-3 py-1.5">
                      <span className="text-[9px] text-violet-600 font-medium block">Grand Total</span>
                      <span className="text-xs font-bold text-violet-800">{editCurSymbol} {editCalculatedTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      {editForm.currency && editForm.currency !== 'INR' && (
                        <span className="text-[9px] text-violet-500 block">≈ ₹{(editCalculatedTotal * (parseFloat(editForm.conversion_factor) || 1)).toLocaleString('en-IN', { minimumFractionDigits: 2 })} (internal)</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              (!po.items || po.items.length === 0) ? (
                <p className="text-xs text-muted-foreground text-center py-4">No items linked</p>
              ) : (
                <div className="space-y-2">
                  {po.items.map((item, idx) => (
                    <div key={idx} className="p-2 rounded border bg-muted/30">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium"><EntityLink type="item" id={item.item_id}>{item.item_name}</EntityLink></p>
                          <p className="text-[10px] text-muted-foreground font-mono"><EntityLink type="item" id={item.item_id} className="font-mono text-[10px] text-muted-foreground">{item.item_unique_id}</EntityLink></p>
                          {item.due_date ? <p className="text-[10px] text-slate-500">Due: <span className="font-mono">{item.due_date}</span></p> : null}
                        </div>
                        <div className="text-right">
                          <p className="text-xs font-medium">Qty: {item.quantity}</p>
                          {(() => {
                            const foreign = po.currency && po.currency !== 'INR';
                            const entered = item.unit_price_entered != null ? item.unit_price_entered : item.unit_price;
                            if (!(item.unit_price > 0 || entered > 0)) return null;
                            const lineForeign = (item.quantity || 0) * (entered || 0);
                            return foreign ? (
                              <p className="text-[10px] text-muted-foreground">
                                {poCurSymbol}{Number(entered).toLocaleString('en-IN')} | Total: {poCurSymbol}{lineForeign.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                <span className="block text-[9px] text-slate-400">≈ ₹{(item.quantity * item.unit_price).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                              </p>
                            ) : (
                              <p className="text-[10px] text-muted-foreground">₹{item.unit_price} | Total: ₹{(item.quantity * item.unit_price).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                            );
                          })()}
                        </div>
                      </div>
                      {item.description && <p className="text-[10px] text-muted-foreground mt-1">{item.description}</p>}
                    </div>
                  ))}
                  {po.value > 0 && (
                    <div className="flex justify-end pt-1">
                      <div className="text-right bg-slate-50 border rounded px-3 py-1.5">
                        <span className="text-[9px] text-muted-foreground block">PO Value</span>
                        {po.currency && po.currency !== 'INR' ? (
                          <>
                            <span className="text-xs font-bold">{poCurSymbol}{Number(po.value_entered != null ? po.value_entered : 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                            <span className="text-[9px] text-muted-foreground block">≈ ₹{po.value.toLocaleString('en-IN', { minimumFractionDigits: 2 })} (internal)</span>
                          </>
                        ) : (
                          <span className="text-xs font-bold">₹{po.value.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )
            )}
          </CardContent>
        </Card>
      </div>

      {/* Contract Review Section */}
      <Card data-testid="contract-review-section">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-muted-foreground" />
              <CardTitle className="text-sm font-medium">Contract Review</CardTitle>
              <Badge variant="outline" className={`text-[10px] ${reviewInfo.color}`}>
                <ReviewIcon className="w-3 h-3 mr-1" />
                {reviewInfo.label}
              </Badge>
            </div>
            <div className="flex gap-2">
              {reviewStatus !== 'closed' && !po.acknowledgment_sent && (
                <Button size="sm" variant="outline" onClick={openAckModal} disabled={sendingAck} data-testid="send-ack-btn">
                  <Mail className="w-3.5 h-3.5 mr-1" /> Send Acknowledgment
                </Button>
              )}
              {reviewStatus !== 'closed' && (po.review_files?.length > 0 || false) && (
                <Button size="sm" onClick={handleCloseReview} data-testid="close-review-btn">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Close Review
                </Button>
              )}
              {reviewStatus === 'closed' && isManager && (
                <Button size="sm" variant="outline" onClick={handleReopenReview} data-testid="reopen-review-btn">
                  <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reopen
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Review status info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div className="p-2.5 rounded-md bg-muted/40">
              <p className="text-muted-foreground text-[10px] mb-0.5">Acknowledgment</p>
              <p className="font-medium">{po.acknowledgment_sent ? `Sent ${po.acknowledgment_sent_at?.slice(0,10) || ''}` : 'Not sent'}</p>
            </div>
            <div className="p-2.5 rounded-md bg-muted/40">
              <p className="text-muted-foreground text-[10px] mb-0.5">Review Opened</p>
              <p className="font-medium">{po.review_opened_at ? po.review_opened_at.slice(0,10) : '-'}</p>
            </div>
            <div className="p-2.5 rounded-md bg-muted/40">
              <p className="text-muted-foreground text-[10px] mb-0.5">Review Documents</p>
              <p className="font-medium">{po.review_files?.length || 0} uploaded</p>
            </div>
            <div className="p-2.5 rounded-md bg-muted/40">
              <p className="text-muted-foreground text-[10px] mb-0.5">Review Closed</p>
              <p className="font-medium">{po.review_closed_at ? `${po.review_closed_at.slice(0,10)} by ${po.review_closed_by_name || ''}` : '-'}</p>
            </div>
          </div>

          {/* Review document upload + list */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-muted-foreground">Signed Contract Review Documents</p>
              {reviewStatus !== 'closed' && (
                <div>
                  <input type="file" id="review-doc-upload" className="hidden" onChange={handleReviewUpload} data-testid="review-doc-upload-input" />
                  <Button size="sm" variant="outline" onClick={() => document.getElementById('review-doc-upload').click()} disabled={uploadingReview} data-testid="upload-review-doc-btn">
                    <Upload className="w-3.5 h-3.5 mr-1" /> {uploadingReview ? 'Uploading...' : 'Upload Review Doc'}
                  </Button>
                </div>
              )}
            </div>
            {(!po.review_files || po.review_files.length === 0) ? (
              <div className="text-center py-4 rounded-md border border-dashed border-muted-foreground/30">
                <ShieldCheck className="w-8 h-8 mx-auto text-muted-foreground/30 mb-1" />
                <p className="text-xs text-muted-foreground">No review documents uploaded yet.</p>
                {reviewStatus !== 'closed' && <p className="text-[10px] text-muted-foreground mt-0.5">Upload the signed contract review form to complete the review.</p>}
              </div>
            ) : (
              <div className="space-y-1.5">
                {po.review_files.map(f => (
                  <div key={f.id} className="flex items-center justify-between p-2.5 rounded-md border bg-emerald-50/50" data-testid={`review-file-${f.id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <ShieldCheck className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs font-medium truncate">{f.original_name}</p>
                        <p className="text-[10px] text-muted-foreground">
                          {formatSize(f.size)} &middot; {f.uploaded_by_name} &middot; {new Date(f.uploaded_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleFileDownload(f)} title="Download">
                        <Download className="w-3.5 h-3.5" />
                      </Button>
                      {reviewStatus !== 'closed' && (
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={() => handleFileDelete(f.id)} title="Delete">
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* PO Files */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium">PO Attached Files ({po.files?.length || 0})</CardTitle>
            <div>
              <input type="file" id="po-file-upload" className="hidden" onChange={handleFileUpload} data-testid="po-file-upload-input" />
              <Button size="sm" variant="outline" onClick={() => document.getElementById('po-file-upload').click()} disabled={uploading} data-testid="upload-po-file-btn">
                <Upload className="w-3.5 h-3.5 mr-1" /> {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {(!po.files || po.files.length === 0) ? (
            <p className="text-xs text-muted-foreground text-center py-4">No files. Upload PO copy, terms, drawings, etc.</p>
          ) : (
            <div className="space-y-2">
              {po.files.map(f => (
                <div key={f.id} className="flex items-center justify-between p-2.5 rounded-md border bg-muted/30" data-testid={`po-file-${f.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs font-medium truncate">{f.original_name}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {formatSize(f.size)} &middot; {f.uploaded_by_name} &middot; {new Date(f.uploaded_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleFileDownload(f)} title="Download" data-testid={`download-po-file-${f.id}`}>
                      <Download className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={() => handleFileDelete(f.id)} title="Delete" data-testid={`delete-po-file-${f.id}`}>
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Send Acknowledgment Modal */}
      <Dialog open={showAckModal} onOpenChange={setShowAckModal}>
        <DialogContent>
          <DialogHeader><DialogTitle className="text-sm flex items-center gap-2"><Mail className="w-4 h-4" /> Send PO Acknowledgment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Sending acknowledgment for <strong>{po?.po_number}</strong> to <strong>{po?.customer_name}</strong>. A copy will be CC&apos;d to you.</p>
            <div>
              <Label className="text-xs">Email Recipients *</Label>
              <Input className="h-8 text-xs mt-1" value={ackEmails}
                onChange={e => setAckEmails(e.target.value)}
                placeholder="Comma-separated emails"
                data-testid="ack-email-input" />
              <p className="text-[10px] text-muted-foreground mt-1">Edit to change recipients. Separate multiple emails with commas.</p>
            </div>
            <Button size="sm" className="w-full" onClick={handleSendAcknowledgment} disabled={sendingAck} data-testid="confirm-send-ack-btn">
              <Mail className="w-3.5 h-3.5 mr-1" /> {sendingAck ? 'Sending...' : 'Send Acknowledgment'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Close PO Dialog */}
      <Dialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
        <DialogContent className="max-w-md" data-testid="close-po-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-violet-800">
              <CheckCircle2 className="w-5 h-5" /> Close Purchase Order
            </DialogTitle>
            <DialogDescription>Closing PO {po.po_number}. This action requires remarks.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Remarks *</Label>
              <textarea
                value={closeRemarks}
                onChange={e => setCloseRemarks(e.target.value)}
                className="w-full h-20 text-xs border rounded-md p-2 mt-1 resize-none"
                placeholder="Reason for closing this PO (e.g., all items dispatched, order cancelled by customer, etc.)"
                data-testid="close-po-remarks"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowCloseDialog(false)}>Cancel</Button>
            <Button size="sm" className="bg-violet-600 hover:bg-violet-700" onClick={handleClosePO} disabled={closing || !closeRemarks.trim()} data-testid="confirm-close-po-btn">
              {closing ? 'Closing...' : 'Close PO'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reopen PO Dialog */}
      <Dialog open={showReopenDialog} onOpenChange={setShowReopenDialog}>
        <DialogContent className="max-w-md" data-testid="reopen-po-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-amber-600" /> Reopen Purchase Order
            </DialogTitle>
            <DialogDescription>Reopening PO {po.po_number}. Status will revert to: {po.pre_close_status || 'active'}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Remarks *</Label>
              <textarea
                value={reopenRemarks}
                onChange={e => setReopenRemarks(e.target.value)}
                className="w-full h-20 text-xs border rounded-md p-2 mt-1 resize-none"
                placeholder="Reason for reopening this PO..."
                data-testid="reopen-po-remarks"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowReopenDialog(false)}>Cancel</Button>
            <Button size="sm" onClick={handleReopenPO} disabled={reopening || !reopenRemarks.trim()} data-testid="confirm-reopen-po-btn">
              {reopening ? 'Reopening...' : 'Reopen PO'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={convDialogOpen} onOpenChange={setConvDialogOpen}>
        <DialogContent className="max-w-sm" data-testid="edit-conversion-factor-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Conversion factor for {pendingCurrency}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">1 {pendingCurrency} = ? INR</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">1 {pendingCurrency} =</span>
              <Input type="number" value={convFactorInput} onChange={e => setConvFactorInput(e.target.value)}
                placeholder="e.g. 90" className="h-8 text-xs w-32" autoFocus data-testid="edit-conversion-factor-input" />
              <span className="text-sm font-medium">INR</span>
            </div>
            <p className="text-[10px] text-muted-foreground">
              Editing the currency/factor recomputes the stored INR values for all items on save.
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConvDialogOpen(false)} data-testid="edit-conversion-cancel-btn">Cancel</Button>
            <Button type="button" onClick={confirmEditConversionFactor} data-testid="edit-conversion-confirm-btn">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoRow({ label, value, mono }) {
  return (
    <div className="flex items-baseline justify-between py-0.5">
      <span className="text-[11px] text-muted-foreground">{label}</span>
      <span className={`text-xs font-medium text-right ${mono ? 'font-mono' : ''}`}>{value || '-'}</span>
    </div>
  );
}
